"use client";

import { useQuery } from "@tanstack/react-query";
import type { IncomeRecord } from "@aera/data-table";
import type { LiffFinancialWorkCard } from "@/lib/financialWork/types";
import { fetchBillLinIncomeRecord } from "@/lib/financialWork/fetchBillLinIncomeRecord";
import { createMarketApiFetch } from "@/lib/marketApiFetch";

const marketApiFetch = createMarketApiFetch();

function incomeRecordTarget(card: LiffFinancialWorkCard | null | undefined): {
  recordId: string;
  workerId: string;
} | null {
  if (!card) return null;
  const focusedEvidenceNav = card.evidenceItems?.find(
    (item) =>
      item.matchedObjectId === card.objectRef.id &&
      item.dataNav?.workerId === "bill-lin-income" &&
      item.dataNav.recordId?.trim(),
  )?.dataNav;
  const incomeEvidenceNav = card.evidenceItems?.find(
    (item) =>
      item.dataNav?.workerId === "bill-lin-income" &&
      item.dataNav.recordId?.trim(),
  )?.dataNav;
  const actionNav = card.availableActions.find(
    (action) => action.actionType === "open_data",
  )?.dataNav;
  const nav = focusedEvidenceNav ?? incomeEvidenceNav ?? actionNav;
  const workerId = nav?.workerId?.trim() || "";
  const recordId = nav?.recordId?.trim() || "";
  if (workerId !== "bill-lin-income" || !recordId) return null;
  return { recordId, workerId };
}

export function financialWorkIncomeRecordTarget(
  card: LiffFinancialWorkCard | null | undefined,
) {
  return incomeRecordTarget(card);
}

export function useFinancialWorkIncomeRecord(
  card: LiffFinancialWorkCard | null | undefined,
  workspaceId: string | null | undefined,
) {
  const target = incomeRecordTarget(card);
  const ws = workspaceId?.trim() || "";

  return useQuery<IncomeRecord | null>({
    queryKey: ["fw-income-record", ws, target?.recordId ?? ""],
    queryFn: () =>
      fetchBillLinIncomeRecord(ws, target!.recordId, marketApiFetch),
    enabled: Boolean(target && ws),
    staleTime: 30_000,
  });
}
